export const SUPPORTED_CURRENCIES = ['SAR', 'USD', 'EUR'];
export const DEFAULT_CURRENCY = 'SAR';

const MINOR_UNITS = {
  SAR: 2,
  USD: 2,
  EUR: 2,
};

export function currencyMinorUnits(currency = DEFAULT_CURRENCY) {
  const code = String(currency || DEFAULT_CURRENCY).toUpperCase();
  return MINOR_UNITS[code] ?? 2;
}

export function isSupportedCurrency(currency) {
  return SUPPORTED_CURRENCIES.includes(String(currency || '').toUpperCase());
}

export function fromMinor(minor, currency = DEFAULT_CURRENCY) {
  if (minor == null || minor === '') {
    return '';
  }
  const amount = Number(minor);
  if (!Number.isFinite(amount)) {
    return '';
  }
  const scale = currencyMinorUnits(currency);
  return (amount / (10 ** scale)).toFixed(scale);
}

export function toMinor(decimalValue, currency = DEFAULT_CURRENCY) {
  const scale = currencyMinorUnits(currency);
  const normalized = String(decimalValue ?? '').trim();
  if (!isValidDecimalAmount(normalized, currency)) {
    return null;
  }
  const [whole, fraction = ''] = normalized.split('.');
  const paddedFraction = fraction.padEnd(scale, '0').slice(0, scale);
  return Number(`${whole}${paddedFraction}`);
}

export function isValidDecimalAmount(value, currency = DEFAULT_CURRENCY) {
  if (value == null) {
    return false;
  }
  const text = String(value).trim();
  const scale = currencyMinorUnits(currency);
  const pattern = new RegExp(`^\\d+(\\.\\d{1,${scale}})?$`);
  return pattern.test(text);
}

export function isPositiveInteger(value) {
  if (value == null || value === '') {
    return false;
  }
  const text = String(value).trim();
  if (!/^[1-9]\d*$/.test(text)) {
    return false;
  }
  const number = Number(text);
  return Number.isInteger(number) && number > 0;
}

export function formatMoney(minor, currency = DEFAULT_CURRENCY, locale = 'en') {
  const decimal = fromMinor(minor, currency);
  if (decimal === '') {
    return '—';
  }
  const amount = Number(decimal);
  if (!Number.isFinite(amount)) {
    return '—';
  }
  try {
    return new Intl.NumberFormat(locale === 'ar' ? 'ar' : 'en', {
      style: 'currency',
      currency: String(currency || DEFAULT_CURRENCY).toUpperCase(),
      minimumFractionDigits: currencyMinorUnits(currency),
      maximumFractionDigits: currencyMinorUnits(currency),
    }).format(amount);
  } catch {
    return `${decimal} ${String(currency || DEFAULT_CURRENCY).toUpperCase()}`;
  }
}

export function planFormFromResource(plan = {}) {
  return {
    name_ar: plan.name_ar || '',
    name_en: plan.name_en || '',
    description_ar: plan.description_ar || '',
    description_en: plan.description_en || '',
    attempt_credits: plan.attempt_credits == null ? '' : String(plan.attempt_credits),
    price: fromMinor(plan.price_minor, plan.currency || DEFAULT_CURRENCY),
    currency: plan.currency || DEFAULT_CURRENCY,
    validity_days: plan.validity_days == null ? '' : String(plan.validity_days),
    is_active: plan.is_active !== false,
    is_featured: Boolean(plan.is_featured),
    sort_order: plan.sort_order == null ? '0' : String(plan.sort_order),
  };
}

export function planPayloadFromForm(form) {
  const currency = String(form.currency || DEFAULT_CURRENCY).toUpperCase();
  return {
    name_ar: form.name_ar,
    name_en: form.name_en,
    description_ar: form.description_ar || null,
    description_en: form.description_en || null,
    attempt_credits: Number(form.attempt_credits),
    price_minor: toMinor(form.price, currency),
    currency,
    validity_days: form.validity_days === '' || form.validity_days == null ? null : Number(form.validity_days),
    is_active: Boolean(form.is_active),
    is_featured: Boolean(form.is_featured),
    sort_order: form.sort_order === '' || form.sort_order == null ? 0 : Number(form.sort_order),
  };
}

export function validatePlanForm(form, t) {
  const errors = {};
  if (!String(form.name_ar || '').trim()) {
    errors.name_ar = [t('forms.required')];
  }
  if (!String(form.name_en || '').trim()) {
    errors.name_en = [t('forms.required')];
  }
  if (!isPositiveInteger(form.attempt_credits)) {
    errors.attempt_credits = [t('companyPlans.validation.attempts')];
  }
  if (!isSupportedCurrency(form.currency)) {
    errors.currency = [t('companyPlans.validation.currency')];
  }
  if (!isValidDecimalAmount(form.price, form.currency)) {
    errors.price = [t('companyPlans.validation.price')];
  }
  if (form.validity_days !== '' && form.validity_days != null && !isPositiveInteger(form.validity_days)) {
    errors.validity_days = [t('companyPlans.validation.validityDays')];
  }
  if (form.sort_order !== '' && form.sort_order != null && !/^\d+$/.test(String(form.sort_order).trim())) {
    errors.sort_order = [t('companyPlans.validation.sortOrder')];
  }
  return errors;
}

export function mapPlanFieldErrors(errors = {}) {
  const mapped = { ...errors };
  if (mapped.price_minor && !mapped.price) {
    mapped.price = mapped.price_minor;
  }
  return mapped;
}
